/**
 * Plan Form Validation Schemas
 * Aligned with backend validation (apps/api/src/modules/plans/plan.validator.ts)
 * and the flag/cap consistency rules in plan.service.ts.
 */

import { z } from 'zod';
import { PLAN_DURATION_UNITS, type CreateSubscriptionPlanRequest } from '@repo/types';

/** Currencies offered in the admin UI (backend accepts any 3-letter code). */
export const CURRENCY_CODES = [
  'INR',
  'USD',
  'EUR',
  'GBP',
  'AED',
  'AUD',
  'CAD',
  'SGD',
] as const;

export const CURRENCY_LABELS: Record<(typeof CURRENCY_CODES)[number], string> = {
  INR: 'INR — Indian Rupee',
  USD: 'USD — US Dollar',
  EUR: 'EUR — Euro',
  GBP: 'GBP — British Pound',
  AED: 'AED — UAE Dirham',
  AUD: 'AUD — Australian Dollar',
  CAD: 'CAD — Canadian Dollar',
  SGD: 'SGD — Singapore Dollar',
};

/**
 * A required numeric field fed by a text input: an empty string becomes NaN so
 * it fails as "required" rather than silently coercing to 0.
 */
const requiredNumber = (opts: { int?: boolean; min: number; label: string }) => {
  let schema = z
    .number({ error: `${opts.label} is required` })
    .min(opts.min, `${opts.label} must be at least ${opts.min}`);
  if (opts.int) schema = schema.int(`${opts.label} must be a whole number`);
  return z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? NaN : Number(v)),
    schema
  );
};

/** An optional non-negative integer cap fed by a text input (blank → 0). */
const optionalCap = (label: string) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? 0 : Number(v)),
    z.number({ error: `${label} must be a number` }).int().min(0, `${label} cannot be negative`)
  );

export const planFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(100, 'Name must not exceed 100 characters'),
    slug: z
      .string()
      .trim()
      .min(1, 'Slug is required')
      .max(100, 'Slug must not exceed 100 characters')
      .regex(/^[a-z0-9-]+$/, 'Use lowercase letters, numbers and hyphens only'),
    tier: requiredNumber({ int: true, min: 0, label: 'Tier' }),
    price: requiredNumber({ min: 0, label: 'Price' }),
    currency: z.enum(CURRENCY_CODES),
    duration_value: requiredNumber({ int: true, min: 1, label: 'Duration value' }),
    duration_unit: z.enum(PLAN_DURATION_UNITS),
    daily_new_jobs_visible: optionalCap('Daily new jobs visible'),
    job_recommendation_enabled: z.boolean(),
    daily_jobs_recommended: optionalCap('Daily jobs recommended'),
    auto_apply_enabled: z.boolean(),
    daily_jobs_applied: optionalCap('Daily jobs applied'),
    is_internal: z.boolean(),
    features: z.string(),
    active: z.boolean(),
  })
  // Mirror the backend: a cap must be 0 while its feature flag is off.
  .superRefine((data, ctx) => {
    if (!data.job_recommendation_enabled && data.daily_jobs_recommended !== 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['daily_jobs_recommended'],
        message: 'Must be 0 while job recommendations are disabled',
      });
    }
    if (!data.auto_apply_enabled && data.daily_jobs_applied !== 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['daily_jobs_applied'],
        message: 'Must be 0 while auto-apply is disabled',
      });
    }
  });

/** The raw form shape (before zod coercion) — what the inputs are bound to. */
export type PlanFormInput = z.input<typeof planFormSchema>;
/** The validated, coerced output (numbers are real numbers). */
export type PlanFormValues = z.output<typeof planFormSchema>;

export const defaultPlanFormValues: PlanFormInput = {
  name: '',
  slug: '',
  tier: '',
  price: '',
  currency: 'INR',
  duration_value: '',
  duration_unit: 'month',
  daily_new_jobs_visible: '',
  job_recommendation_enabled: false,
  daily_jobs_recommended: '',
  auto_apply_enabled: false,
  daily_jobs_applied: '',
  is_internal: false,
  features: '',
  active: true,
};

/** Map validated form values into the API request body. */
export function toCreatePlanRequest(values: PlanFormValues): CreateSubscriptionPlanRequest {
  return {
    name: values.name.trim(),
    slug: values.slug.trim(),
    tier: values.tier,
    price: values.price,
    currency: values.currency,
    duration_value: values.duration_value,
    duration_unit: values.duration_unit,
    daily_new_jobs_visible: values.daily_new_jobs_visible,
    job_recommendation_enabled: values.job_recommendation_enabled,
    daily_jobs_recommended: values.job_recommendation_enabled ? values.daily_jobs_recommended : 0,
    auto_apply_enabled: values.auto_apply_enabled,
    daily_jobs_applied: values.auto_apply_enabled ? values.daily_jobs_applied : 0,
    is_internal: values.is_internal,
    features: values.features
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean),
    active: values.active,
  };
}
